import { useState, useEffect, useCallback, useMemo } from 'react'
import dayjs from 'dayjs'

import {
    fetchAllInvoices,
    updateInvoice,
    createInvoice,
    sendInvoiceEmail,
    getInvoiceById,
    getLastInvoiceNumber,
    fetchOrganizationName,
    bulkUpdateInvoices,
    bulkEmailInvoices,
    bulkZohoSync,
    bulkRaiseInvoices,
} from '../services/invoiceRecords.service'

import {
    fetchBillingRecords,
    updateBillingRecordWithInvoice,
    syncBillingRecordFromInvoice,
} from '../services/billingRecords.service'

import {
    exportToZoho,
    prepareZohoState,
    createZohoInvoicePayload,
} from '../pages/InvoiceRecords/utils/zohoHelpers'

/**
 * Unified Invoice + Billing Records Hook
 * Works for desktop & mobile
 */
const useInvoiceRecords = () => {
    /* ------------------------- State ------------------------- */

    const [invoices, setInvoices] = useState([])
    const [loading, setLoading] = useState(true)

    const [filters, setFilters] = useState({
        plantName: '',
        organization: '',
        status: '',
        period: dayjs(),
        autoReminder: '',
        emailSent: '',
        zohoExported: '',
    })

    const [selectedInvoices, setSelectedInvoices] = useState([])
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(10)

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success',
    })

    const [isExporting, setIsExporting] = useState(false)

    /* --------------------- Snackbar --------------------- */

    const showSnackbar = (message, severity = 'success') => {
        setSnackbar({ open: true, message, severity })
    }

    const closeSnackbar = () => {
        setSnackbar((prev) => ({ ...prev, open: false }))
    }

    /* ------------------ Data Fetch ------------------ */

    const periodKey = filters.period ? dayjs(filters.period).format('YYYY-MM') : ''

    const loadInvoices = useCallback(async () => {
        try {
            setLoading(true)

            // Extract month and year from filters.period
            let month = ''
            let year = ''
            if (filters.period) {
                month = dayjs(filters.period).format('MMMM') // "January", etc.
                year = dayjs(filters.period).format('YYYY')
            }

            const params = {
                skip: 0,
                limit: 5000,
                month,
                year,
            }

            const [invoiceResponse, billingResponse] = await Promise.all([
                fetchAllInvoices(params), // includes bank details now
                fetchBillingRecords({ paymentStatus: 'To Be Raised' }),
            ])

            let combined = []

            /* -------- Invoices -------- */
            if (invoiceResponse?.data) {
                const mappedInvoices = invoiceResponse.data.map(inv => ({
                    ...inv,
                    recordType: 'INVOICE',
                }))
                combined.push(...mappedInvoices)
            }

            /* -------- Billing Records -------- */
            const billingList = Array.isArray(billingResponse)
                ? billingResponse
                : billingResponse?.data || []

            const orgCache = {}

            const mappedBilling = billingList.map(record => ({
                ...record,
                _id: record.id || record._id,
                recordType: 'BILLING',
                invoiceNumber: 'Bill',
                status: 'To Be Raised',
                invoiceValue: record.amount || 0,
                energyValue: record.actualGeneration || 0,
                deemedEnergy: record.deemedGeneration || 0,
                mainTariff: record.tariff || 0,
                organizationName: record.organizationName || null, // Backend should ideally provide this now
            }))

            // Filter billing records by month/year and status
            const filteredBilling = mappedBilling.filter(record => {
                // Period filter (Strict: only current selected month)
                if (filters.period) {
                    const recMonth = dayjs(record.invoiceDate || record.recordDate).format('YYYY-MM')
                    const fltMonth = dayjs(filters.period).format('YYYY-MM')
                    if (recMonth !== fltMonth) return false
                }

                return true
            })

            setInvoices([...combined, ...filteredBilling])
        } catch (err) {
            console.error(err)
            showSnackbar('Failed to load invoice records', 'error')
        } finally {
            setLoading(false)
        }
    }, [periodKey])

    useEffect(() => {
        loadInvoices()
    }, [periodKey, loadInvoices])

    /* ------------------ Filtering & Pagination ------------------ */

    useEffect(() => {
        setPage(0)
    }, [filters.status, filters.plantName, filters.organization, filters.period, filters.autoReminder, filters.emailSent, filters.zohoExported])

    const filteredInvoices = useMemo(() => {
        return invoices.filter(inv => {
            // Status filter
            if (filters.status && filters.status !== 'ALL') {
                const target = filters.status.toUpperCase()
                // Check paymentStatus first for PAID/UNPAID logic as per user request
                const pStatus = inv.paymentStatus?.toUpperCase()
                if (pStatus === target) return true

                // Fallback to general status (e.g. SENT, DRAFT, TO BE RAISED)
                const gStatus = inv.status?.toUpperCase()
                if (gStatus === target) return true

                return false
            }

            // Plant Name filter
            if (filters.plantName && inv.plantName !== filters.plantName) return false

            // Organization filter
            if (
                filters.organization &&
                (inv.organizationName || inv.organizationId) !== filters.organization
            ) return false

            // New Advanced Filters
            if (filters.autoReminder && filters.autoReminder !== 'ALL') {
                const isAuto = inv.automated === true
                if (filters.autoReminder === 'Yes' && !isAuto) return false
                if (filters.autoReminder === 'No' && isAuto) return false
            }

            if (filters.emailSent && filters.emailSent !== 'ALL') {
                const isSent = inv.emailSent === true
                if (filters.emailSent === 'Yes' && !isSent) return false
                if (filters.emailSent === 'No' && isSent) return false
            }

            if (filters.zohoExported && filters.zohoExported !== 'ALL') {
                // Check both sentToZoho flag and existence of zohoinvoicenumber
                const isExported = inv.sentToZoho === true || !!inv.zohoinvoicenumber
                if (filters.zohoExported === 'Yes' && !isExported) return false
                if (filters.zohoExported === 'No' && isExported) return false
            }

            return true
        })
    }, [invoices, filters.status, filters.plantName, filters.organization, filters.autoReminder, filters.emailSent, filters.zohoExported])

    const total = filteredInvoices.length

    const paginatedInvoices = useMemo(() => {
        return filteredInvoices.slice(page * rowsPerPage, (page + 1) * rowsPerPage)
    }, [filteredInvoices, page, rowsPerPage])

    /* ------------------ Derived Data ------------------ */

    const plantNames = [...new Set(invoices.map(i => i.plantName).filter(Boolean))]
    const organizations = [...new Set(invoices.map(i => i.organizationName || i.organizationId).filter(Boolean))]
    const statusOptions = ['PAID', 'UNPAID', 'PENDING', 'OVERDUE', 'TO BE RAISED']

    /* ------------------ Selection ------------------ */

    const handleSelectInvoice = (id) => {
        setSelectedInvoices(prev =>
            prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
        )
    }

    const handleSelectAll = (checked) => {
        if (checked) {
            setSelectedInvoices(
                filteredInvoices
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map(inv => inv._id)
            )
        } else {
            setSelectedInvoices([])
        }
    }

    /* ------------------ Update Invoice ------------------ */

    const handleUpdateInvoice = async (id, updates) => {
        try {
            await updateInvoice(id, updates)

            const invoice = invoices.find(inv => inv._id === id)
            if (invoice?.recordType === 'INVOICE' && (updates.status || updates.paymentStatus)) {
                await syncBillingRecordFromInvoice(invoice.plantId, invoice.invoiceDate, {
                    paymentStatus: updates.status || updates.paymentStatus,
                    invoiceNumber: invoice.invoiceNumber,
                    invoiceDate: updates.invoiceDate || invoice.invoiceDate,
                    dueDate: updates.dueDate || invoice.dueDate,
                    invoiceValue: updates.invoiceValue || invoice.invoiceValue,
                })
            }

            showSnackbar('Invoice updated successfully')
            await loadInvoices()
            setSelectedInvoices([])
        } catch {
            showSnackbar('Failed to update invoice', 'error')
        }
    }

    /* ------------------ Create Invoice ------------------ */

    /*const handleCreateInvoice = async (data) => {
        try {
            const dateStr = dayjs(data.invoiceDate).format('YYYY-MM-DD')
            const last = data.organizationId
                ? await getLastInvoiceNumber(data.organizationId, dateStr)
                : {}

            const payload = {
                ...data,
                invoiceNumber:
                    last?.invoiceNumber ||
                    `INV-${dayjs().format('YYYYMM')}-${Math.floor(Math.random() * 9000) + 1000}`,
            }

            await createInvoice(payload)

            await updateBillingRecordWithInvoice(data.billingRecordId, {
                invoiceNumber: payload.invoiceNumber,
                paymentStatus: payload.paymentStatus,
            })

            showSnackbar('Invoice created successfully')
            loadInvoices()
        } catch {
            showSnackbar('Failed to create invoice', 'error')
        }
    }*/

    const handleCreateInvoice = async (data) => {
        try {
            setLoading(true)

            const dateStr = dayjs(data.invoiceDate).format('YYYY-MM-DD')
            let nextInv = { invoiceNumber: null }

            try {
                if (data.organizationId) {
                    nextInv = await getLastInvoiceNumber(data.organizationId, dateStr)
                }
            } catch (e) {
                console.warn('Failed to fetch last invoice number, fallback used', e)
            }

            const mainEnergy = parseFloat(data.energyValue || data.actualGeneration || 0)
            const mainTariff = parseFloat(data.mainTariff || data.tariff || 0)

            const deemedEnergy = parseFloat(
                data.deemedEnergy || data.deemedGeneration || 0
            )
            const deemedTariff = parseFloat(data.mainTariff || 0)

            const mainAmount = mainEnergy * mainTariff
            const deemedAmount = deemedEnergy * deemedTariff
            const totalAmount = mainAmount + deemedAmount

            const subTotal = totalAmount
            const roundedTotal = Math.round(subTotal)
            const roundingOff = roundedTotal - subTotal

            const orgId =
                typeof data.organizationId === 'object' && data.organizationId?.$oid
                    ? data.organizationId.$oid
                    : data.organizationId

            const payload = {
                invoiceNumber:
                    nextInv.invoiceNumber ||
                    `INV-${dayjs().format('YYYYMM')}-${Math.floor(
                        Math.random() * 9000
                    ) + 1000}`,

                plantId: data.plantId,
                plantName: data.plantName,
                organizationId: orgId ? String(orgId) : null,

                invoiceValue: roundedTotal,
                roundingOff: parseFloat(roundingOff.toFixed(2)),

                energyValue: mainEnergy,
                mainTariff: mainTariff,

                deemedEnergy: deemedEnergy,
                deemedTariff: mainTariff,

                deemedGeneration: parseFloat(deemedAmount.toFixed(2)),

                type: data.type || 'Regular',
                status: 'To Be Exported',
                paymentStatus: 'To Be Exported',

                invoiceDate: data.invoiceDate,
                dueDate: data.dueDate,

                automated: false,
                emailSent: false,

                remarks:
                    data.remarks || 'Invoice generated from billing record',
            }

            /* 5️⃣ Create invoice */
            const response = await createInvoice(payload)

            if (!response?.success) {
                showSnackbar(response?.message || 'Failed to generate invoice', 'error')
                return false
            }

            /* 6️⃣ Sync billing record*/
            try {
                await syncBillingRecordFromInvoice(payload.plantId, payload.invoiceDate, {
                    paymentStatus: payload.paymentStatus,
                    invoiceNumber: payload.invoiceNumber,
                    invoiceDate: payload.invoiceDate,
                    dueDate: payload.dueDate,
                    energyValue: payload.energyValue,
                    deemedEnergy: payload.deemedEnergy,
                    mainTariff: payload.mainTariff,
                    invoiceValue: payload.invoiceValue,
                })
            } catch (syncError) {
                console.warn(
                    'Billing sync failed, falling back to direct update',
                    syncError
                )

                await updateBillingRecordWithInvoice(data.billingRecordId, {
                    paymentStatus: payload.paymentStatus,
                    invoiceNumber: payload.invoiceNumber,
                    invoiceDate: payload.invoiceDate,
                    dueDate: payload.dueDate,
                    message: 'Direct update during invoice creation',
                })
            }

            showSnackbar('Invoice generated successfully', 'success')
            await loadInvoices()
            setSelectedInvoices([])
            return true
        } catch (error) {
            console.error('Error generating invoice:', error)
            showSnackbar('Failed to generate invoice', 'error')
            return false
        } finally {
            setLoading(false)
        }
    }

    /**
     * NEW: Bulk Raise Invoices from Billing Records
     * Iterates through selected billing records and performs individual creation logic
     * but minimizes UI flickering by performing a single reload at the end.
     */
    const handleBulkRaiseInvoices = async (bulkData) => {
        const { invoiceIds, updates } = bulkData;
        setLoading(true);

        try {
            const invoicesToRaise = [];
            for (const id of invoiceIds) {
                const data = invoices.find(inv => inv._id === id);
                if (!data) continue;
                invoicesToRaise.push(data);
            }

            if (invoicesToRaise.length === 0) {
                showSnackbar('No invoices to raise', 'warning');
                return;
            }

            const response = await bulkRaiseInvoices({
                invoices: invoicesToRaise,
                updates: {
                    invoiceDate: updates.invoiceDate ? dayjs(updates.invoiceDate).format('YYYY-MM-DD') : undefined,
                    dueDate: updates.dueDate ? dayjs(updates.dueDate).format('YYYY-MM-DD') : undefined,
                    automated: updates.automated,
                    remarks: updates.remarks,
                }
            });

            if (response?.success) {
                const { successCount, failCount, totalCount } = response;
                let message = `Successfully raised ${successCount} invoice(s).`;
                if (failCount > 0) {
                    message += ` Failed: ${failCount}. Total: ${totalCount}.`;
                    showSnackbar(message, 'warning');
                } else {
                    showSnackbar(message, 'success');
                }
            } else {
                showSnackbar(response?.message || 'Failed to raise invoices', 'error');
            }
        } catch (error) {
            console.error('Bulk raise failed:', error);
            showSnackbar(error.response?.data?.message || 'Bulk raise failed due to an error', 'error');
        } finally {
            await loadInvoices();
            setSelectedInvoices([]);
            setLoading(false);
        }
    };


    /* ------------------ Email ------------------ */

    const handleSendEmail = async (invoiceId, type, pdfBlob) => {
        try {
            const res = await sendInvoiceEmail(invoiceId, type, pdfBlob)
            showSnackbar(res.message, res.success ? 'success' : 'error')
            loadInvoices()
        } catch {
            showSnackbar('Failed to send email', 'error')
        }
    }

    /* ------------------ Zoho Export ------------------ */

    const handleExportToZoho = async (invoiceIds) => {
        setIsExporting(true)
        try {
            for (const id of invoiceIds) {
                const invoice = invoices.find(inv => inv._id === id)
                if (!invoice) continue
                const zohoState = await prepareZohoState(invoice)
                await exportToZoho({ invoice, zohoState })
            }
            showSnackbar('Exported to Zoho successfully')
            loadInvoices()
        } catch {
            showSnackbar('Zoho export failed', 'error')
        } finally {
            setIsExporting(false)
        }
    }

    /* ------------------ CSV Export ------------------ */

    const exportToCSV = () => {
        if (!filteredInvoices.length) return

        const headers = [
            'Plant Name',
            'Organization',
            'Invoice Number',
            'Invoice Date',
            'Due Date',
            'Amount',
            'Status',
        ]

        const rows = filteredInvoices.map(inv => [
            inv.plantName,
            inv.organizationName,
            inv.invoiceNumber,
            inv.invoiceDate,
            inv.dueDate,
            inv.invoiceValue,
            inv.status || inv.paymentStatus,
        ])

        const csv = [headers, ...rows].map(r => r.join(',')).join('\n')
        const blob = new Blob([csv], { type: 'text/csv' })
        const url = URL.createObjectURL(blob)

        const a = document.createElement('a')
        a.href = url
        a.download = `invoice_records_${dayjs().format('YYYYMMDD_HHmmss')}.csv`
        a.click()
        URL.revokeObjectURL(url)
    }

    const handleBulkUpdate = async (bulkData) => {
        try {
            await bulkUpdateInvoices(bulkData)
            showSnackbar(`Successfully updated ${bulkData.invoiceIds.length} invoices`)
            loadInvoices()
            setSelectedInvoices([])
        } catch (error) {
            showSnackbar(error.response?.data?.message || 'Bulk update failed', 'error')
            throw error
        }
    }

    const handleBulkEmail = async (bulkData) => {
        try {
            const result = await bulkEmailInvoices(bulkData)
            showSnackbar(`Sent ${result.successCount} of ${result.totalCount} emails`)
            loadInvoices()
        } catch (error) {
            showSnackbar(error.response?.data?.message || 'Bulk email failed', 'error')
            throw error
        }
    }

    const handleBulkZohoSync = async (invoiceIds) => {
        try {
            setLoading(true)
            const payloads = []

            // Generate payload for each invoice
            for (const id of invoiceIds) {
                const invoice = invoices.find(inv => inv._id === id)
                if (!invoice) continue

                try {
                    const zohoState = await prepareZohoState(invoice)
                    // We only need the inner 'invoice' object for the bulk array
                    // createZohoInvoicePayload returns { invoice: { ... } }
                    const fullPayload = createZohoInvoicePayload(invoice, zohoState)
                    if (fullPayload?.invoice) {
                        payloads.push(fullPayload.invoice)
                    }
                } catch (err) {
                    console.error(`Failed to prepare payload for invoice ${invoice.invoiceNumber}`, err)
                }
            }

            if (payloads.length === 0) {
                showSnackbar('No valid invoices to sync', 'warning')
                setLoading(false)
                return
            }

            // Send aggregated payloads to backend
            const result = await bulkZohoSync({ invoiceIds, invoiceData: payloads })

            // Create a comprehensive notification message
            if (result.success) {
                // All succeeded
                showSnackbar(`Successfully exported all ${result.successCount} invoice(s) to Zoho.`, 'success')
            } else if (result.partialSuccess) {
                // Some succeeded, some failed
                const errorDetails = result.errors?.map(err => `• ${err.error}`).join('\n') || 'Unknown errors'
                const combinedMessage = `Exported ${result.successCount} invoice(s) successfully.\n\n${result.failCount} failed:\n${errorDetails}`

                showSnackbar(combinedMessage, 'warning')
            } else {
                // All failed
                const errorDetails = result.errors?.map(err => `• ${err.error}`).join('\n') || 'Unknown errors'
                const combinedMessage = `Failed to export all ${result.failCount} invoice(s):\n${errorDetails}`

                showSnackbar(combinedMessage, 'error')
            }

            loadInvoices()
        } catch (error) {
            console.error('Bulk sync execution failed:', error)
            showSnackbar(error.response?.data?.message || 'Bulk Zoho sync failed', 'error')
        } finally {
            setLoading(false)
        }
    }

    /* ------------------ Pagination ------------------ */

    const handleChangePage = (_, newPage) => {
        setPage(newPage)
        setSelectedInvoices([])
    }

    const handleChangeRowsPerPage = (e) => {
        setRowsPerPage(parseInt(e.target.value, 10))
        setPage(0)
        setSelectedInvoices([])
    }

    /* ------------------ Public API ------------------ */

    return {
        invoices: paginatedInvoices,
        loading,
        filters,
        setFilters,
        selectedInvoices,
        setSelectedInvoices,
        page,
        rowsPerPage,
        total,
        snackbar,
        plantNames,
        organizations,
        statusOptions,
        isExporting,

        handleSelectInvoice,
        handleSelectAll,
        handleUpdateInvoice,
        handleCreateInvoice,
        handleSendEmail,
        handleExportToZoho,
        handleBulkUpdate,
        handleBulkRaiseInvoices,
        handleBulkEmail,
        handleBulkZohoSync,
        exportToCSV,

        handleChangePage,
        handleChangeRowsPerPage,
        closeSnackbar,
        loadInvoices,
    }
}

export default useInvoiceRecords
